import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OfficeLocations = () => {
  const [selectedOffice, setSelectedOffice] = useState('mumbai'); // Changed initial selected office

  const offices = [
    {
      id: 'mumbai',
      city: 'Mumbai',
      country: 'India',
      address: 'B-Wing, 18th Floor, One BKC, G Block BKC\nBandra Kurla Complex, Bandra East\nMumbai, Maharashtra 400051, India',
      phone: '+91 22 6602 3000',
      email: 'mumbai@onsmettle.com',
      timezone: 'IST (UTC+5:30)',
      hours: 'Mon-Fri: 9:30 AM - 6:30 PM',
      lat: '19.0760',
      lng: '72.8777',
      teamSize: '60+ professionals',
      specialties: ['BFSI', 'IT Consulting', 'Digital Transformation'],
      available: true
    },
    {
      id: 'bengaluru',
      city: 'Bengaluru',
      country: 'India',
      address: 'Level 14, Concorde Tower, UB City\n1 Vittal Mallya Road\nBengaluru, Karnataka 560001, India',
      phone: '+91 80 4040 2000',
      email: 'bengaluru@onsmettle.com',
      timezone: 'IST (UTC+5:30)',
      hours: 'Mon-Fri: 9:00 AM - 6:00 PM',
      lat: '12.9716',
      lng: '77.5946',
      teamSize: '55+ professionals',
      specialties: ['Product Development', 'AI/ML Solutions', 'Startup Consulting'],
      available: true
    },
    
  ];

  const selectedOfficeData = offices.find(office => office.id === selectedOffice);

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/20 to-secondary/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-heading">
            Global Presence, Local Expertise
          </h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            With offices across major business hubs, we're positioned to serve clients worldwide while maintaining the personal touch of local partnerships.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Office Selection */}
          <div className="lg:col-span-1">
            <h3 className="text-xl font-semibold text-gray-800 mb-6">Our Locations</h3>
            <div className="space-y-3">
              {offices.map((office) => (
                <div
                  key={office.id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    selectedOffice === office.id
                      ? 'border-brand-cyan bg-brand-cyan/20 backdrop-blur-sm'
                      : 'glass-morph bg-white/70 border-gray-200 hover:border-brand-cyan/50'
                  }`}
                  onClick={() => setSelectedOffice(office.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">{office.city}</h4>
                      <p className="text-sm text-gray-700">{office.country}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${office.available ? 'bg-brand-green' : 'bg-warning'}`}></div>
                      <span className="text-xs text-gray-600">
                        {office.available ? 'Available' : 'Busy'}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{office.teamSize}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Office Details */}
          <div className="lg:col-span-2">
            <div className="glass-morph bg-white/90 backdrop-blur-md border border-gray-300 rounded-xl p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {selectedOfficeData.city}, {selectedOfficeData.country}
                  </h3>
                  <div className="flex items-center mt-2">
                    <div className={`w-3 h-3 rounded-full mr-2 ${selectedOfficeData.available ? 'bg-brand-green' : 'bg-warning'}`}></div>
                    <span className="text-sm font-medium text-gray-700">
                      {selectedOfficeData.available ? 'Team Available Now' : 'Currently in Meetings'}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">{selectedOfficeData.timezone}</p>
                  <p className="text-sm font-medium text-gray-900">{selectedOfficeData.hours}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-4">Contact Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <Icon name="MapPin" size={18} className="text-brand-cyan mr-3 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-700 whitespace-pre-line">
                          {selectedOfficeData.address}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <Icon name="Phone" size={18} className="text-primary mr-3 flex-shrink-0" />
                      <a 
                        href={`tel:${selectedOfficeData.phone}`}
                        className="text-sm text-primary hover:underline"
                      >
                        {selectedOfficeData.phone}
                      </a>
                    </div>
                    
                    <div className="flex items-center">
                      <Icon name="Mail" size={18} className="text-primary mr-3 flex-shrink-0" />
                      <a 
                        href={`mailto:${selectedOfficeData.email}`}
                        className="text-sm text-primary hover:underline"
                      >
                        {selectedOfficeData.email}
                      </a>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Specialties</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedOfficeData.specialties.map((specialty, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-brand-cyan/20 text-brand-cyan border border-brand-cyan/30 text-xs font-medium rounded-full"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-4">Location Map</h4>
                  <div className="w-full h-48 glass-morph bg-white/40 backdrop-blur-sm border border-gray-200 rounded-lg overflow-hidden">
                    <iframe
                      width="100%"
                      height="100%"
                      loading="lazy"
                      title={`${selectedOfficeData.city} Office Location`}
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps?q=${selectedOfficeData.lat},${selectedOfficeData.lng}&z=14&output=embed`}
                      className="border-0"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t border-gray-300">
                <Button
                  variant="default"
                  className="flex-1"
                >
                  <Icon name="Phone" size={20} className="mr-2" />
                  Call This Office
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                >
                  <Icon name="Calendar" size={20} className="mr-2" />
                  Schedule Visit
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                >
                  <Icon name="Navigation" size={20} className="mr-2" />
                  Get Directions
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OfficeLocations;